import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { useEffect, useState } from 'react';

export const FinanceDashboardReservesChart = ({
  startPeriod,
  endPeriod,
  periodicity,
  prodIds,
  channelIds,
  chartType,
  entity,
}) => {
  const rsrc = useRsrc();
  const [data, setData] = useState();
  const { navigateTo } = useNavState();

  useEffect(() => {
    if (!prodIds?.length) return;
    if (!channelIds?.length) return;

    const ndc11ProdIds = prodIds.map((prod) => prod);
    const reqParam = { chartType, startPeriod, endPeriod, periodicity, ndc11ProdIds, channelIds, entity };

    rsrc.data('gtn', 'RESERVES_DASHBOARD_DATA_PLUGIN', reqParam, entity).then((rows) => setData(rows));
  }, [chartType, startPeriod, endPeriod, periodicity, prodIds, channelIds, entity]);

  if (!data) return null;

  const handleClick = (clickData) => {
    const code = clickData.category; // e.g. "2024Q4" or "2024M11"
    let PAID_PERIOD = [data.periodIdMap[clickData.category]];
    if (periodicity === 'QTR') {
      const periodCodes = expandQuarterToMonths(code); // ["2024M10", "2024M11", "2024M12"]

      PAID_PERIOD = periodCodes.map((c) => data.periodIdMap[c]).filter(Boolean);
    }

    navigateTo('gtn/reserve', {
      mode: Mode.PERSPECTIVE,
      perspectiveState: {
        dimFilters: {
          CHANNEL: channelIds,
          PROD: prodIds,
          PAID_PERIOD: PAID_PERIOD,
        },
        dimToPeriodicities: {
          PAID_PERIOD: [periodicity],
          EARNED_PERIOD: ['QTR'],
        },
      },
    });
  };

  // Helper: expand a quarterly code into its months
  function expandQuarterToMonths(periodCode) {
    const match = periodCode.match(/^(\d{4})Q([1-4])$/);
    if (!match) return [periodCode]; // Not a quarter → return as-is

    const year = match[1];
    const quarter = Number(match[2]);

    // Define month suffixes for each quarter
    const quarterMonths = {
      1: ['M01', 'M02', 'M03'],
      2: ['M04', 'M05', 'M06'],
      3: ['M07', 'M08', 'M09'],
      4: ['M10', 'M11', 'M12'],
    };

    // Build the monthly codes
    return quarterMonths[quarter].map((m) => `${year}${m}`);
  }

  const coolColors = [
    '#1E90FF',
    '#00BFFF',
    '#20B2AA',
    '#2E8B57',
    '#3CB371',
    '#32CD32',
    '#00CED1',
    '#5F9EA0',
    '#40E0D0',
    '#66CDAA',
  ];

  const enrichSeries = (series, lineIndexes = []) => {
    return series.map((s, i) => {
      // Default color rotation
      let color = coolColors[i % coolColors.length];

      // Override colors for specific labels
      if (s.label === 'Accruals') {
        color = '#FFA500'; // orange/yellow
      } else if (s.label === 'Payments') {
        color = '#FF4500'; // red/orange-red
      }

      return {
        ...s,
        type: s.type || (lineIndexes.includes(i) ? 'line' : 'column'),
        color,
      };
    });
  };

  const chartData = {
    ...data,
    series: enrichSeries(data.series, [1]), // here [1] means "second series is line"
  };

  return <CartesianChart data={chartData} vertical stacked markers={true} onClick={handleClick} />;
};
